import dayjs from 'dayjs';
import { ReactNode, useCallback } from 'react';
import { PartnerTimeZoneTooltip } from 'src/components';
import { ConversationType, conversationTypeOptionsMap } from 'src/graphql';
import { ActivityFeed } from 'src/graphql/entities/activity-feed/hooks/use-activity-feed';
import { DATE_FORMAT } from 'src/pages/Settings/users/const';
import { useDisplayTime } from 'src/utils/hooks';
import { ActivityFeedTime } from './acitivity-feed-time';
import { ChatActivityContainer } from './chat-activity-container';
import { StyledTypography } from './styled/typography.styled';

export const UpdateConversationActivityMessage = ({
  activity,
}: {
  activity: ActivityFeed;
}) => {
  const { timeFormat } = useDisplayTime();

  const formatValue = useCallback(
    (value: string | null, customType: string): ReactNode => {
      if (!value) return value;

      switch (customType) {
        case 'ConversationType':
          return conversationTypeOptionsMap.get(value as ConversationType)?.label ?? value;
        case 'Date': {
          const formattedDate = dayjs(value).format(`${DATE_FORMAT} [at] ${timeFormat}`);
          return (
            <PartnerTimeZoneTooltip date={value} partnerId={activity.partnerId}>
              {formattedDate}
            </PartnerTimeZoneTooltip>
          );
        }
        default:
          return value;
      }
    },
    [activity.partnerId, timeFormat],
  );

  return activity.changes
    .filter(({ value, previousValue }) => value !== previousValue)
    .map((activityFeedChange) => {
      if (!activityFeedChange.customType) return activityFeedChange;
      return {
        ...activityFeedChange,
        value: formatValue(activityFeedChange.value, activityFeedChange.customType),
        previousValue: formatValue(
          activityFeedChange.previousValue,
          activityFeedChange.customType,
        ),
      };
    })
    .map(({ name, value, previousValue }) => {
      let title: ReactNode = null;
      if (!!value && !!previousValue) {
        title = (
          <StyledTypography>
            <b>{activity.actor.name}</b> changed <b>{name}</b> from <b>{previousValue}</b> to{' '}
            <b>{value}</b> <ActivityFeedTime activity={activity} />
          </StyledTypography>
        );
      } else if (!!value && !previousValue) {
        title = (
          <StyledTypography>
            <b>{activity.actor.name}</b> set <b>{name}</b> to <b>{value}</b>{' '}
            <ActivityFeedTime activity={activity} />
          </StyledTypography>
        );
      } else if (!value && !!previousValue) {
        title = (
          <StyledTypography>
            <b>{activity.actor.name}</b> unset <b>{name}</b> from <b>{previousValue}</b>{' '}
            <ActivityFeedTime activity={activity} />
          </StyledTypography>
        );
      }
      return <ChatActivityContainer key={name} icon="commit" title={title} />;
    });
};
